import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store, createActionGroup, createSelector, emptyProps, props } from '@ngrx/store';
import {QueryRef} from 'apollo-angular';

import { filter, map, switchMap } from 'rxjs';
import { SubSink } from 'subsink';

import {
    DeleteInvoicesGQL,
  ExportInvoiceToQuickbooksGQL,
  FinalizeInvoicesGQL,
  FullInvoiceFragment,
  GetInvoiceLinksGQL,
  InvoiceDetailsGQL,
  InvoiceDetailsQuery,
  InvoiceDetailsQueryVariables,
  UpdateInvoicesGQL,
  UpdateInvoicesMutationVariables,
} from '../../../generated/graphql.generated';
import { isAegisArtifact } from '../../artifacts/artifact.util';
import { JobInvoiceActions } from '../../job/job-invoice/job-invoice.actions';
import { selectInvoices } from '../../job/job-tool.reducer';
import { remainingBalance } from '../../jobs/jobs.util';
import { DetailsHelperService } from '../../services/details-helper.service';
import { DocumentHelperService } from '../../services/document-helper.service';
import { FreyaHelperService } from '../../services/freya-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { PermissionService } from '../../services/permission.service';
import { isDocumentRefreshRefusedError } from '../../utilities/errors.util';
import { DisabledWhen, DisabledWhenTooltips, KarveMenuItem, setMenuItemDisabled } from '../../utilities/menu-item.util';
import { isDraftInvoice, isFinalizedInvoice, outdatedInvoiceReason, shouldRefreshInvoiceDocument } from '../invoices.utils';

export const InvoiceDetailsActions = createActionGroup({
  source: 'Invoice Details Panel',
  events: {
    'Send Invoice Menu Item Clicked': props<{
      invoiceId: string;
      isFinalized: boolean;
    }>(),
    'Open Invoice As Menu Item Clicked': emptyProps(),
  },
});

const selectInvoice = (invoiceId: string) => createSelector(
  selectInvoices,
  (invoices) => {
    return invoices.find((i) => i.id === invoiceId);
  },
);

@Component({
  selector: 'app-invoice-details',
  templateUrl: './invoice-details.component.html',
  styleUrls: ['./invoice-details.component.scss']
})
export class InvoiceDetailsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() invoiceId: string;

  invoice: FullInvoiceFragment;

  invoiceQueryRef: QueryRef<InvoiceDetailsQuery, InvoiceDetailsQueryVariables>;

  documentUpdating = false;

  subs = new SubSink();

  showDownloadInvoiceLink = false;

  showSendInvoiceLink = false;

  showOpenInvoiceLink = false;

  viewOnQuickbooksUrl = 'https://app.qbo.intuit.com/app/invoice?txnId=';

  permissions = {
    pay: false,
    send: false,
    update: false,
    delete: false,
    exportToQuickbooks: false,
  };

  invoiceActions: KarveMenuItem[] = [
    {
      id: 'pay',
      label: 'Pay',
      icon: 'pi pi-wallet',
      command: () => this.documentHelper.payInvoice(this.invoice),
      disabled: true,
      disabledWhen: {
        invoiceVoided: true,
        objectDeleted: true,
        invoicePaid: true,
      }
    },
    {
      id: 'preview',
      label: 'Preview',
      icon: 'pi pi-eye',
      command: () => this.viewDocument(),
      disabled: true,
      disabledWhen: {
        documentGenerating: true,
      }
    },
    {
      id: 'download',
      label: 'Download',
      icon: 'pi pi-download',
      command: () => this.downloadDocument(),
      disabled: true,
      disabledWhen: {
        documentGenerating: true,
        objectDeleted: true,
      }
    },
    {
      id: 'send',
      label: 'Send',
      icon: 'pi pi-send',
      command: () => {
        this.store.dispatch(InvoiceDetailsActions.sendInvoiceMenuItemClicked({
          invoiceId: this.invoiceId,
          isFinalized: isFinalizedInvoice(this.invoice),
        }));
        this.sendInvoice();
      },
      disabled: true,
      disabledWhen: {
        documentGenerating: true,
        invoiceVoided: true,
        objectDeleted: true,
        hasPlaceholderEmail: true,
      }
    },
    {
      id: 'employee',
      label: 'View as Employee',
      icon: 'pi pi-external-link',
      command: () => this.confirmOpenInvoice('employee'),
      disabled: true,
      disabledWhen: {
        documentGenerating: true,
        invoiceVoided: true,
        objectDeleted: true,
      }
    },
    {
      id: 'finalize',
      label: 'Finalize',
      icon: 'pi pi-check',
      command: () => this.finalizeInvoice(),
      disabled: true,
      disabledWhen: {
        documentGenerating: true,
        invoiceVoided: true,
        objectDeleted: true,
      }
    },
    {
      id: 'customer',
      label: 'View as Customer',
      icon: 'pi pi-external-link',
      command: () => this.confirmOpenInvoice('customer'),
      disabled: true,
      disabledWhen: {
        documentGenerating: true,
        invoiceVoided: true,
        objectDeleted: true,
      }
    },
    {
      id: 'void',
      label: 'Void',
      icon: 'pi pi-times',
      command: () => this.voidInvoice(),
      disabled: true,
      disabledWhen: {
        invoiceVoided: true,
      },
      visible: false,
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'pi pi-trash',
      command: () => this.deleteInvoice(),
      visible: false,
    },
    {
      id: 'export-to-quickbooks',
      label: 'Export to Quickbooks',
      icon: 'pi pi-share-alt',
      visible: false,
      command: () => this.exportToQuickbooks(),
      disabledWhen: {
        invoiceExportedToQuickbooks: true,
        invoiceVoided: true,
        invoiceDraft: true,
        documentGenerating: true,
        objectDeleted: true,
      },
    },
    {
      id: 'force-update',
      label: 'Force Update Invoice',
      icon: 'pi pi-sync',
      command: () => this.updateDocument(),
      disabled: true,
      disabledWhen: {
        documentGenerating: true,
        invoiceVoided: true,
        objectDeleted: true,
      }
    },
  ];

  constructor(
    private invoiceDetailsGQL: InvoiceDetailsGQL,
    private permissionHandler: PermissionService,
    private localNotify: FreyaNotificationsService,
    private documentHelper: DocumentHelperService,
    private freyaHelper: FreyaHelperService,
    private detailsHelper: DetailsHelperService,
    private updateInvoicesGQL: UpdateInvoicesGQL,
    private getInvoiceLinksGQL: GetInvoiceLinksGQL,
    private finalizeInvoicesGQL: FinalizeInvoicesGQL,
    private deleteInvoicesGQL: DeleteInvoicesGQL,
    private exportInvoiceToQuickbooksGQL: ExportInvoiceToQuickbooksGQL,
    private store: Store,
    private actions: Actions,
  ) { }

  ngOnInit(): void {

    this.setPermissions();

    this.subs.sink = this.detailsHelper.getObjectUpdates([ 'Invoice', 'Jobs' ]).subscribe((update) => {

      if (!this.invoiceId) { return; }

      const invoiceJobUpdated = update.type === 'Jobs' && update.id === this.invoice?.job?.id;

      if (update.type === 'Invoice' || invoiceJobUpdated) {
        this.retrieveInvoice();
      }
    });


    // Update UI when invoice is updated
    this.subs.sink = this.actions.pipe(
      ofType(
        JobInvoiceActions.voidInvoiceSuccess,
        JobInvoiceActions.getInvoiceLinkSuccess,
        JobInvoiceActions.finalizeInvoiceSuccess,
        JobInvoiceActions.forceUpdateInvoiceSuccess,
        JobInvoiceActions.sendInvoiceSuccess,
        JobInvoiceActions.deleteInvoiceSuccess,
      ),
      map((action) => action.invoiceId),
      switchMap((invoiceId) => this.store.select(selectInvoice(invoiceId))),
      filter((invoice) => invoice.id === this.invoiceId),
      // Do not overwrite events or transactions as the store's invoice contains no events or transactions
      map((invoice) => ({
        ...invoice,
        events: this.invoice?.events || [],
        transactions: this.invoice?.transactions || [],
      })),
    ).subscribe((invoice) => this.invoice = invoice);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('invoiceId') && this.invoiceId) {
      this.retrieveInvoice();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  retrieveInvoice() {
    if (this.invoiceQueryRef) {
      this.invoiceQueryRef.refetch({ invoiceId: this.invoiceId });
      return;
    }

    this.invoiceQueryRef = this.invoiceDetailsGQL.watch({ invoiceId: this.invoiceId });

    this.subs.sink = this.invoiceQueryRef.valueChanges.subscribe((res) => {

      if (res.loading) { return; }

      this.invoice = res.data.invoices.invoices?.[0];

      this.setShowLinks();

      this.setDisabledActions();

      this.setActionsVisibility();

    });
  }

  setPermissions() {
    this.subs.sink = this.permissionHandler.watchPermissions([
      'transactions.create',
      'invoice.send',
      'invoice.update',
      'invoice.delete',
      'quickbooks.createInvoice'
    ]).subscribe((res) => {

      this.permissions.pay = res[0];
      this.permissions.send = res[1];
      this.permissions.update = res[2];
      this.permissions.delete = res[3];
      this.permissions.exportToQuickbooks = res[4];

      this.setActionsVisibility();
    });
  }

  confirmOpenInvoice(role: 'customer' | 'employee') {

    const message = `Opening an invoice will finalize it. `
    + `You will not be able to make any further charges on any invoiced events. `
    + `If you just want to view the document and do not need to collect signatures, `
    + `consider using the "Preview" option instead.`;

    if (isFinalizedInvoice(this.invoice) || isAegisArtifact(this.artifact)) {
      this.openInvoice(role, false);
    } else {
      this.freyaHelper.confirmWithLoading({
        header: 'Open Invoice?',
        message,
        acceptLabel: 'Open and Finalize',
        acceptIcon: 'pi pi-external-link',
        rejectLabel: 'Preview',
        rejectIcon: 'pi pi-eye',
        reject: () => this.viewDocument(),
        acceptObservable: this.getInvoiceLinksGQL.mutate({ invoiceIds: [ this.invoiceId ], role }),
        onAcceptSuccess: (res) => {

          const [ link ] = res.data.getLinks;

          if (!link) {
            this.localNotify.error('Unable to open invoice');
          } else {

            this.freyaHelper.openInDialog(this.artifact, link);
            this.store.dispatch(JobInvoiceActions.getInvoiceLinkSuccess({
              jobId: this.invoice.job.id,
              invoiceId: this.invoiceId,
              link,
              promptedBy: InvoiceDetailsActions.openInvoiceAsMenuItemClicked(),
            }));
          }

          this.localNotify.success('Invoice finalized');

          this.detailsHelper.pushUpdate({
            id: this.invoiceId,
            action: 'update',
            type: 'Invoice',
          });
        },
        onAcceptErr: (err) => {
          this.localNotify.apolloError('Unable to open invoice', err);
        },
      });
    }
  }

  openInvoice(role: 'customer' | 'employee', willFinalize: boolean) {

    this.getInvoiceLinksGQL.mutate({ invoiceIds: [ this.invoiceId ], role }).subscribe((res) => {

      const [ link ] = res.data.getLinks;

      if (!link) {
        this.localNotify.error('Unable to open invoice');


      } else {

        this.store.dispatch(JobInvoiceActions.getInvoiceLinkSuccess({
          jobId: this.invoice.job.id,
          invoiceId: this.invoiceId,
          link,
          promptedBy: InvoiceDetailsActions.openInvoiceAsMenuItemClicked(),
        }));

        this.freyaHelper.openInDialog(this.artifact, link);
      }

      if (willFinalize) {
        this.localNotify.success('Invoice finalized');

        this.detailsHelper.pushUpdate({
          id: this.invoiceId,
          action: 'update',
          type: 'Invoice',
        });
      }
    }, (err) => {
      this.localNotify.apolloError('Unable to open invoice', err);
    });
  }

  finalizeInvoice() {
    this.finalizeInvoicesGQL.mutate({ invoiceIds: [ this.invoiceId ] }).subscribe((res) => {

      if (!res.data?.finalizeInvoices) {
        this.store.dispatch(JobInvoiceActions.finalizeInvoiceError({ error: new Error('Failed to finalize invoice') }));
        return;
      }

      this.store.dispatch(JobInvoiceActions.finalizeInvoiceSuccess({
        invoiceId: this.invoiceId,
        jobId: this.invoice.job.id,
      }));

    }, (error) => {
      this.store.dispatch(JobInvoiceActions.finalizeInvoiceError({ error }));
    });
  }

  get artifact() {
    return this.invoice?.artifacts?.[0];
  }

  setDisabledActions() {

    const disabledWhen: DisabledWhen = {
      documentGenerating: this.documentGenerating,
      invoiceVoided: this.invoiceVoided,
      objectDeleted: this.objectDeleted,
      invoicePaid: this.invoicePaid,
      invoiceExportedToQuickbooks: this.invoiceExportedToQuickbooks,
      invoiceDraft: this.invoiceDraft,
      invoiceFinalized: Boolean(this.invoice?.sentAt) || Boolean(this.invoice?.openedAt),
      hasPlaceholderEmail: this.artifact?.metadata?.placeholderEmail,
    };

    const overrideDefaultTooltips: DisabledWhenTooltips = {
      objectDeleted: 'This invoice was deleted',
    };

    for (const action of this.invoiceActions) {
      setMenuItemDisabled(action, disabledWhen, overrideDefaultTooltips);
    }

  }

  setActionsVisibility() {
    const voidAction = this.invoiceActions.find((a) => a.id === 'void');

    if (!voidAction) {
      throw new Error('Could not find void action');
    }

    const deleteAction = this.invoiceActions.find((a) => a.id === 'delete');

    if (!deleteAction) {
      throw new Error('Could not find delete action');
    }

    const payAction = this.invoiceActions.find((a) => a.id === 'pay');

    if (!payAction) {
      return new Error('Could not find pay action');
    }

    const sendAction = this.invoiceActions.find((a) => a.id === 'send');

    if (!sendAction) {
      throw new Error('Could not find send action');
    }

    const openAsCustomerAction = this.invoiceActions.find((a) => a.id === 'customer');

    if (!openAsCustomerAction) {
      throw new Error('Could not find open as customer action');
    }

    const openAsEmployeeAction = this.invoiceActions.find((a) => a.id === 'employee');

    if (!openAsEmployeeAction) {
      throw new Error('Could not find open as employee action');
    }

    const exportToQuickbooksAction = this.invoiceActions.find((a) => a.id === 'export-to-quickbooks');

    if (!exportToQuickbooksAction) {
      throw new Error('Could not find export to Quickbooks action');
    }

    payAction.visible = this.permissions.pay;

    voidAction.visible = Boolean(this.invoice?.sentAt || this.invoice?.openedAt) && this.permissions.update;

    deleteAction.visible = Boolean(!this.invoice?.sentAt && !this.invoice?.deletedAt) && this.permissions.delete;

    sendAction.visible = this.permissions.send;

    openAsEmployeeAction.visible = this.permissions.update;

    openAsCustomerAction.visible = this.permissions.update;

    exportToQuickbooksAction.visible = this.permissions.exportToQuickbooks;
  }

  viewDocument() {
    this.refreshThenOpen(
      (outdatedReason) => this.freyaHelper.openInDialog(this.artifact, undefined, { outdatedReason }),
      'Failed to update and preview invoice',
    );
  }

  private refreshThenOpen(open: (outdatedReason?: string) => void, failureMessage: string) {

    if (!shouldRefreshInvoiceDocument(this.invoice)) {
      open();
      return;
    }

    const vars: UpdateInvoicesMutationVariables = {
      invoices: {
        invoiceId: this.invoiceId,
        updatePricingTables: true,
      },
    };

    this.documentUpdating = true;

    this.updateInvoicesGQL.mutate(vars)
      .subscribe((_res) => {

        this.detailsHelper.pushUpdate({
          id: this.invoiceId,
          type: 'Invoice',
          action: 'update',
        });

        this.documentUpdating = false;

        this.store.dispatch(JobInvoiceActions.forceUpdateInvoiceSuccess({
          invoiceId: this.invoice.id,
          jobId: this.invoice.job.id,
        }));
        open();

      }, (err) => {
        this.documentUpdating = false;

        if (isDocumentRefreshRefusedError(err)) {
          open(outdatedInvoiceReason(err));
          return;
        }

        this.localNotify.apolloError(failureMessage, err);
      });
  }

  voidInvoice() {

    const vars: UpdateInvoicesMutationVariables = {
      invoices: [{
        invoiceId: this.invoiceId,
        voided: true,
      }],
    };

    this.updateInvoicesGQL.mutate(vars).subscribe(() => {

      this.localNotify.success('Invoice voided');

      this.detailsHelper.pushUpdate({
        id: this.invoiceId,
        type: 'Invoice',
        action: 'update',
      });

      this.store.dispatch(JobInvoiceActions.voidInvoiceSuccess({
        jobId: this.invoice.job.id,
        invoiceId: this.invoiceId,
      }));

    }, (err) => {

      this.localNotify.apolloError('Failed to void invoice', err);
    });
  }

  updateDocument() {

    const vars: UpdateInvoicesMutationVariables = {
      invoices: {
        invoiceId: this.invoiceId,
        updatePricingTables: true,
      },
    };

    this.documentUpdating = true;

    this.updateInvoicesGQL.mutate(vars)
    .subscribe((_res) => {
      this.localNotify.success('Invoice updated');

      this.detailsHelper.pushUpdate({
        id: this.invoiceId,
        type: 'Invoice',
        action: 'update',
      });

      this.documentUpdating = false;

      this.store.dispatch(JobInvoiceActions.forceUpdateInvoiceSuccess({
        invoiceId: this.invoice.id,
        jobId: this.invoice.job.id,
      }));

    }, (err) => {
      this.localNotify.apolloError('Failed to update invoice', err);
      this.documentUpdating = false;
    });

  }

  deleteInvoice() {
    this.deleteInvoicesGQL.mutate({ invoiceIds: [ this.invoiceId ]})
      .subscribe(() => {

        this.localNotify.success('Invoice deleted');

        this.detailsHelper.pushUpdate({
          type: 'Invoice',
          id: this.invoiceId,
          action: 'delete',
        });

        this.store.dispatch(JobInvoiceActions.deleteInvoiceSuccess({
          jobId: this.invoice.job.id,
          invoiceId: this.invoiceId,
        }));

      }, (err) => {

        this.localNotify.apolloError('Failed to delete invoice', err);

      });
  }

  downloadDocument() {
    this.refreshThenOpen(
      (outdatedReason) => {
        if (outdatedReason) {
          this.documentHelper.warnInvoiceOutdated(outdatedReason);
        }
        this.documentHelper.downloadDocument(this.artifact, { outdated: Boolean(outdatedReason) });
      },
      'Failed to update and download invoice',
    );
  }

  sendInvoice() {
    this.documentHelper.confirmSendInvoice(this.invoice);
  }

  exportToQuickbooks() {

    const timeout = setTimeout(() => {
      this.localNotify.info('Exporting to Quickbooks', 'This may take a few seconds');
    }, 200);

    const failMsg = 'Failed to export invoice to Quickbooks';
    this.exportInvoiceToQuickbooksGQL.mutate({ invoiceId: this.invoiceId }).subscribe({
      next: (res) => {

        clearTimeout(timeout);

        if (!res.data.exportInvoiceToQuickbooks) {
          this.localNotify.error(failMsg, 'Please make sure that Quickbooks integration is connected and enabled');
          return;
        }

        this.localNotify.success('Invoice successfully exported to Quickbooks');

        this.detailsHelper.pushUpdate({
          id: this.invoiceId,
          type: 'Invoice',
          action: 'update',
        });

      },
      error: (err) => {
        this.localNotify.apolloError(failMsg, err);
        clearTimeout(timeout);
      },
    });
  }

  setShowLinks() {

    const downloadDisabled = this.documentGenerating || this.objectDeleted;

    this.showDownloadInvoiceLink = this.artifact && !downloadDisabled;

    const openInvoiceDisabled = this.documentGenerating || this.invoiceVoided || this.objectDeleted;

    this.showOpenInvoiceLink = !this.invoice.openedAt && !openInvoiceDisabled;

    const sendInvoiceDisabled = this.documentGenerating || this.invoiceVoided || this.objectDeleted;

    this.showSendInvoiceLink = !this.invoice.sentAt && !sendInvoiceDisabled;
  }

  get documentGenerating() {
    return !(this.artifact?.url || this.artifact?.signedUrl);
  }

  get objectDeleted() {
    return Boolean(this.invoice?.deletedAt);
  }

  get invoiceVoided() {
    return Boolean(this.invoice?.voidedAt);
  }

  get invoicePaid() {
    return remainingBalance(this.invoice) === 0;
  }

  get invoiceExportedToQuickbooks() {
    return Boolean(this.invoice?.metadata?.quickbooksId);
  }

  get invoiceDraft() {
    return isDraftInvoice(this.invoice);
  }

}
